import { createClient } from "@launchdarkly/js-client-sdk";
import * as amplitude from "@amplitude/analytics-browser";
import { getOrCreateDeviceId } from "../lib/deviceId";

export const DEFAULT_PHONE_NUMBER = "+16462410636";
export const DEFAULT_MESSAGE = "Hey Iris";

const IRIS_PHONE_FLAG_KEY = "iris-phone-number";
const PHONE_REGEX = /^\+\d{7,20}$/;

const LD_CLIENT_KEYS = {
  prod: "67bc6d6428801b0bf9c71f56",
  stage: "67bc6d6428801b0bf9c71f55",
} as const;

const AMPLITUDE_API_KEYS = {
  prod: "60e805b112c5df5ce8b452b20e06531f",
  stage: "60e805b112c5df5ce8b452b20e06531f",
} as const;

export type IrisEnvironment = keyof typeof LD_CLIENT_KEYS;

export type LaunchDarklyClientLike = {
  start?: () => void;
  waitForInitialization?: () => Promise<unknown>;
  variation: (flagKey: string, defaultValue: string) => unknown;
};

export type AmplitudeClientLike = {
  track: (
    eventName: string,
    properties?: Record<string, unknown>,
  ) => { promise?: Promise<unknown> };
  flush: () => { promise?: Promise<unknown> };
  getDeviceId: () => string | undefined;
};

export type IrisCtaOptions = {
  source?: string;
  // Same value as Amplitude's device id.
  deviceId?: string;
  // Logical page name for LaunchDarkly targeting. Defaults to the current path.
  pageName?: string;
  ldClient?: LaunchDarklyClientLike;
  amplitudeClient?: AmplitudeClientLike;
  environment?: IrisEnvironment;
  phoneNumber?: string;
  message?: string;
  fallbackScheme?: string;
  theme?: "light" | "dark";
};

type WindowWithTracking = Window & {
  amplitude?: {
    track?: (
      eventName: string,
      properties: Record<string, unknown>,
    ) => { promise?: Promise<unknown> };
    flush?: () => { promise?: Promise<unknown> };
    getDeviceId?: () => string;
  };
  fbq?: (...args: unknown[]) => void;
  ttq?: {
    track?: (event: string, data?: Record<string, unknown>) => void;
  };
};

const ldClientPromiseCache = new Map<
  string,
  Promise<LaunchDarklyClientLike | null>
>();

const amplitudeClientPromiseCache = new Map<
  string,
  Promise<AmplitudeClientLike | null>
>();

const getRuntimeWindow = () => {
  if (typeof window === "undefined") return null;
  return window as WindowWithTracking;
};

const getDeviceId = (options: IrisCtaOptions = {}) => {
  if (options.deviceId) return options.deviceId;

  const runtimeWindow = getRuntimeWindow();
  if (!runtimeWindow) return undefined;

  const amplitudeDeviceId = runtimeWindow.amplitude?.getDeviceId?.();
  if (amplitudeDeviceId) return amplitudeDeviceId;

  try {
    return getOrCreateDeviceId();
  } catch {
    return undefined;
  }
};

const getPageName = (options: IrisCtaOptions = {}) => {
  if (options.pageName) return options.pageName;

  const runtimeWindow = getRuntimeWindow();
  if (!runtimeWindow) return undefined;

  const pathname = runtimeWindow.location.pathname;
  if (!pathname || pathname === "/") return "home";
  return pathname.replace(/\/+$/, "").replace(/^\//, "") || "home";
};

const normalizePhone = (candidate: unknown) => {
  if (typeof candidate !== "string") return null;
  return PHONE_REGEX.test(candidate) ? candidate : null;
};

export const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);

const buildFallbackIMessageUrl = (
  phoneNumber: string,
  options: IrisCtaOptions = {},
) => {
  const deviceId = getDeviceId(options);
  const baseMessage = options.message ?? DEFAULT_MESSAGE;
  const body = deviceId
    ? `${baseMessage}! My invite code is ${deviceId}`
    : baseMessage;

  let protocol: "imessage://" | "sms://";
  if (options.fallbackScheme) {
    const base = options.fallbackScheme.split("?")[0];
    protocol = /^sms:\/\//i.test(base) ? "sms://" : "imessage://";
  } else {
    protocol = isIOS() ? "imessage://" : "sms://";
  }

  return `${protocol}${phoneNumber}?body=${encodeURIComponent(body)}`;
};

const getEnvironmentClient = async (options: IrisCtaOptions = {}) => {
  const environment = options.environment;
  if (!environment) return null;

  const sdkKey = LD_CLIENT_KEYS[environment];
  if (!sdkKey) return null;

  const deviceId = getDeviceId(options);
  if (!deviceId) return null;

  const pageName = getPageName(options);
  const cacheKey = `${environment}:${deviceId}:${pageName ?? ""}`;
  const existing = ldClientPromiseCache.get(cacheKey);
  if (existing) return existing;

  const created = (async () => {
    try {
      const client = createClient(sdkKey, {
        kind: "device_id",
        key: deviceId,
        ...(pageName ? { pageName } : {}),
      });
      client.start?.();
      await client.waitForInitialization?.().catch(() => undefined);
      return client;
    } catch {
      return null;
    }
  })();

  ldClientPromiseCache.set(cacheKey, created);
  return created;
};

const getAmplitudeEnvironmentClient = async (options: IrisCtaOptions = {}) => {
  const environment = options.environment;
  if (!environment) return null;

  const apiKey = AMPLITUDE_API_KEYS[environment];
  if (!apiKey) return null;

  const deviceId = getDeviceId(options);
  if (!deviceId) return null;

  const cacheKey = `${environment}:${deviceId}`;
  const existing = amplitudeClientPromiseCache.get(cacheKey);
  if (existing) return existing;

  const created = (async () => {
    try {
      const instance = amplitude.createInstance();
      await instance.init(apiKey, { deviceId }).promise;
      return {
        track: (eventName: string, properties?: Record<string, unknown>) =>
          instance.track(eventName, properties),
        flush: () => instance.flush(),
        getDeviceId: () => instance.getDeviceId(),
      } satisfies AmplitudeClientLike;
    } catch {
      return null;
    }
  })();

  amplitudeClientPromiseCache.set(cacheKey, created);
  return created;
};

const getPhoneFromLaunchDarkly = async (options: IrisCtaOptions = {}) => {
  const ldClient = options.ldClient ?? (await getEnvironmentClient(options));

  if (!ldClient?.variation) return null;

  // Ensure client is initialized before reading flags
  // This prevents "Unknown feature flag" warnings when client hasn't loaded yet
  if (ldClient.waitForInitialization) {
    await ldClient.waitForInitialization().catch(() => undefined);
  }

  const candidate = ldClient.variation(
    IRIS_PHONE_FLAG_KEY,
    DEFAULT_PHONE_NUMBER,
  );
  return normalizePhone(candidate);
};

export const resolvePhoneNumber = async (options: IrisCtaOptions = {}) => {
  const overridePhone = normalizePhone(options.phoneNumber);
  if (overridePhone) return overridePhone;

  const ldPhone = await getPhoneFromLaunchDarkly(options);
  if (ldPhone) return ldPhone;

  return DEFAULT_PHONE_NUMBER;
};

export const getInitialIMessageUrl = (options: IrisCtaOptions = {}) => {
  const fallbackPhone =
    normalizePhone(options.phoneNumber) ?? DEFAULT_PHONE_NUMBER;
  return buildFallbackIMessageUrl(fallbackPhone, options);
};

export const resolveIMessageUrl = async (options: IrisCtaOptions = {}) => {
  const phoneNumber = await resolvePhoneNumber(options);
  return buildFallbackIMessageUrl(phoneNumber, options);
};

export const trackIrisCtaClick = (source = "webflow") => {
  try {
    if (typeof window === "undefined") return;

    const runtimeWindow = window as WindowWithTracking;

    if (runtimeWindow.amplitude?.track) {
      const runtimeDeviceId = runtimeWindow.amplitude.getDeviceId?.();
      const trackResult = runtimeWindow.amplitude.track(
        "imessage_iris_clicked",
        {
          source,
          device_id: runtimeDeviceId,
        },
      );
      trackResult?.promise?.catch((error: unknown) => {
        console.error(error);
      });

      const flushResult = runtimeWindow.amplitude.flush?.();
      flushResult?.promise?.catch((error: unknown) => {
        console.error(error);
      });
    }

    runtimeWindow.fbq?.("track", "Lead");
    runtimeWindow.ttq?.track?.("ClickButton");
  } catch (error) {
    console.error(error);
  }
};

export const buildIrisCtaTrackingPayload = (options: IrisCtaOptions = {}) => {
  const payload: Record<string, unknown> = {
    source: options.source ?? "webflow",
    theme: options.theme ?? "light",
  };

  const deviceId = getDeviceId(options);
  if (deviceId) {
    payload.device_id = deviceId;
  }

  return payload;
};

export const trackIrisCtaClickWithOptions = async (
  options: IrisCtaOptions = {},
) => {
  try {
    if (typeof window === "undefined") return;

    const runtimeWindow = window as WindowWithTracking;
    const payload = buildIrisCtaTrackingPayload(options);

    // Prefer provided client, then lazy-create for third-party usage
    const amplitudeClient =
      options.amplitudeClient ?? (await getAmplitudeEnvironmentClient(options));

    if (amplitudeClient?.track) {
      const trackResult = amplitudeClient.track(
        "imessage_iris_clicked",
        payload,
      );
      if (
        trackResult &&
        typeof trackResult === "object" &&
        "promise" in trackResult
      ) {
        (trackResult as { promise?: Promise<unknown> }).promise?.catch(
          (error: unknown) => {
            console.error(error);
          },
        );
      }

      const flushResult = amplitudeClient.flush?.();
      if (
        flushResult &&
        typeof flushResult === "object" &&
        "promise" in flushResult
      ) {
        (flushResult as { promise?: Promise<unknown> }).promise?.catch(
          (error: unknown) => {
            console.error(error);
          },
        );
      }
    }

    runtimeWindow.fbq?.("track", "Lead");
    runtimeWindow.ttq?.track?.("ClickButton");
  } catch (error) {
    console.error(error);
  }
};
